import { supabase } from "@/lib/supabase";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const DEFAULT_MESSAGES = {
  0: "Can't reach ChatSleek. Check your connection and try again.",
  401: "Please sign in to continue.",
  403: "You don't have access to that.",
  404: "We couldn't find what you were looking for. It may have been deleted.",
  409: "That couldn't be completed right now. Please try again.",
  413: "That's too large to upload.",
  429: "Too many requests. Please wait a moment.",
  500: "Something went wrong. Please try again.",
  502: "ChatSleek is temporarily unavailable. Please try again.",
  503: "ChatSleek is temporarily unavailable. Please try again.",
};

export class ApiError extends Error {
  constructor(status, code, message, fields, requestId) {
    super(message || DEFAULT_MESSAGES[status] || DEFAULT_MESSAGES[500]);
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.requestId = requestId || null;
  }
}

async function currentToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function toApiError(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const err = body?.error;
  const requestId = err?.request_id || res.headers.get("X-Request-ID");
  if (!err) console.warn(`[ChatSleek] ${res.status} without error body`, { url: res.url, requestId });
  return new ApiError(res.status, err?.code || "http_error", err?.message, err?.fields, requestId);
}

export async function apiFetch(path, { method = "GET", body, signal, auth = true, raw = false, headers = {} } = {}) {
  const binary = body instanceof Blob;
  const send = (token) =>
    fetch(`${BASE}${path}`, {
      method,
      signal,
      headers: {
        ...headers,
        ...(body !== undefined && !binary ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : binary ? body : JSON.stringify(body),
    });

  let token = null;
  if (auth) {
    token = await currentToken();
    if (!token) throw new ApiError(401, "unauthenticated");
  }
  let res;
  try {
    res = await send(token);
    if (res.status === 401 && auth) {
      const { data } = await supabase.auth.refreshSession();
      if (data.session) res = await send(data.session.access_token);
      if (res.status === 401) await supabase.auth.signOut({ scope: "local" });
    }
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    if (e instanceof ApiError) throw e;
    throw new ApiError(0, "network_error");
  }
  if (!res.ok) throw await toApiError(res);
  if (raw) return res;
  return res.status === 204 ? null : res.json();
}

export const api = {
  get: (path, opts) => apiFetch(path, opts),
  post: (path, body, opts) => apiFetch(path, { ...opts, method: "POST", body }),
  patch: (path, body, opts) => apiFetch(path, { ...opts, method: "PATCH", body }),
  put: (path, body, opts) => apiFetch(path, { ...opts, method: "PUT", body }),
  del: (path, body, opts) => apiFetch(path, { ...opts, method: "DELETE", body }),
};
